/**
 * ŠABLÓNA "template_1" – PORTRAIT.
 * Pôvodný (doteraz jediný) dizajn obrazovky denného menu na výšku.
 * Registruje sa v ./dailyMenuTemplates.js pod id "template_1".
 */
import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useRotatingLocale } from "./useRotatingLocale";
import {
  useDailyMenuScreenData,
  COUNTDOWN_INTERVAL_MS,
} from "app/hooks/dsok/useDailyMenuScreenData";
import ErrorView from "./ErrorView";
import { usePaging } from "./hooks/usePaging";
import Ticker from "./components/Ticker";
import WeeklyMenuPortrait from "./components/WeeklyMenuPortrait";
import { DailyMenuContentList, DailyMenuContentGrid } from "./content";
import "./DailyMenuScreen.css";
import "./DailyMenuScreenPortrait.css";

const PORTRAIT_PAGE_INTERVAL_MS = 8000;
/* Grid portrait: polievky stránkovanie len ak viac ako 3 (3 na stránku). Hlavné jedlá stránkovanie len ak viac ako 9 (9 na stránku). */
const GRID_PORTRAIT_SOUPS_PER_PAGE = 3;
const GRID_PORTRAIT_MEALS_PER_PAGE = 9;
const GRID_PORTRAIT_SOUPS_PAGING_THRESHOLD = 3;
const GRID_PORTRAIT_MEALS_PAGING_THRESHOLD = 9;
const TICKER_HEIGHT_PX = 48;
const ALLERGENS_BAR_HEIGHT_PX = 72;
const DATE_TIME_LOCALE = "sk-SK";

/* Odhad výšky: header ~70px, ticker 48px, padding + nadpisy sekcií + medzery ~120px => miesto pre riadky */
const PORTRAIT_HEADER_PX = 70;
const PORTRAIT_RESERVED_PX = 120;
const PORTRAIT_ROW_HEIGHT_PX = 130;

/**
 * Koľko položiek sa zmestí na portrait obrazovku (súčet riadkov polievok + jedál).
 * Rozdelí priestor približne 40% polievky / 60% jedlá.
 */
function getPortraitCounts(innerHeight) {
  const h = innerHeight || 1920;
  const available =
    h - PORTRAIT_HEADER_PX - TICKER_HEIGHT_PX - PORTRAIT_RESERVED_PX;
  const totalRows = Math.max(1, Math.floor(available / PORTRAIT_ROW_HEIGHT_PX));
  const maxSoups = Math.max(1, Math.floor(totalRows * 0.4));
  const maxMeals = Math.max(1, totalRows - maxSoups);
  return { maxSoups, maxMeals };
}

function formatDateOnly(date) {
  try {
    const formatted = new Intl.DateTimeFormat(DATE_TIME_LOCALE, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
    return formatted.replace(/\s/g, "");
  } catch {
    return date.toLocaleDateString(DATE_TIME_LOCALE).replace(/\s/g, "");
  }
}

const DailyMenuScreenPortrait = () => {
  const { customerCode, locationCode } = useParams();
  const [searchParams] = useSearchParams();
  const env = searchParams.get("env") ?? "";

  const {
    loading,
    error,
    refetch,
    logo,
    activeMenuType,
    defaultLocale,
    supportedLocales,
    rotateMs,
    soups,
    meals,
    getTitle,
    getAllergensLegend,
    getTickerText,
    showAllergens,
    showDescription,
    showPrices,
    productDisplayType,
    soupPlaceholderImage,
    mealPlaceholderImage,
    weekDays,
    weeklyRotation,
  } = useDailyMenuScreenData({
    customerCode,
    locationCode,
    env,
    menuId: "default",
    orientation: "portrait",
  });

  const [locale] = useRotatingLocale(supportedLocales, rotateMs);

  // Striedanie denného a týždenného menu (LEN portrait). Aktívne iba keď je
  // zapnuté nastavenie a existuje aspoň jeden deň s obsahom.
  const weeklyRotationActive =
    weeklyRotation?.enabled === true && (weekDays?.length ?? 0) > 0;
  const [showWeekly, setShowWeekly] = useState(false);
  useEffect(() => {
    if (!weeklyRotationActive) {
      setShowWeekly(false);
      return undefined;
    }
    const intervalMs = Math.max(1, weeklyRotation.intervalSec) * 1000;
    const id = setInterval(() => setShowWeekly((s) => !s), intervalMs);
    return () => clearInterval(id);
  }, [weeklyRotationActive, weeklyRotation?.intervalSec]);

  const [gridSoupsPageIndex, setGridSoupsPageIndex] = useState(0);
  const [gridMealsPageIndex, setGridMealsPageIndex] = useState(0);

  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1080,
    height: typeof window !== "undefined" ? window.innerHeight : 1920,
  }));
  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { maxMeals } = useMemo(
    () => getPortraitCounts(windowSize.height),
    [windowSize.height],
  );

  const visibleSoups = soups;
  const { visibleItems: visibleMeals } = usePaging(
    meals,
    maxMeals,
    PORTRAIT_PAGE_INTERVAL_MS,
  );

  /* Grid portrait: polievky stránkovanie len ak viac ako 3; hlavné jedlá len ak viac ako 9 */
  const gridSoupsPaging = soups.length > GRID_PORTRAIT_SOUPS_PAGING_THRESHOLD;
  const gridMealsPaging = meals.length > GRID_PORTRAIT_MEALS_PAGING_THRESHOLD;
  const gridSoupsPageCount = gridSoupsPaging
    ? Math.max(1, Math.ceil(soups.length / GRID_PORTRAIT_SOUPS_PER_PAGE))
    : 1;
  const gridMealsPageCount = gridMealsPaging
    ? Math.max(1, Math.ceil(meals.length / GRID_PORTRAIT_MEALS_PER_PAGE))
    : 1;
  const gridSoupsStart = gridSoupsPaging
    ? gridSoupsPageIndex * GRID_PORTRAIT_SOUPS_PER_PAGE
    : 0;
  const gridMealsStart = gridMealsPaging
    ? gridMealsPageIndex * GRID_PORTRAIT_MEALS_PER_PAGE
    : 0;
  const visibleGridSoups = gridSoupsPaging
    ? soups.slice(gridSoupsStart, gridSoupsStart + GRID_PORTRAIT_SOUPS_PER_PAGE)
    : soups;
  const visibleGridMeals = gridMealsPaging
    ? meals.slice(gridMealsStart, gridMealsStart + GRID_PORTRAIT_MEALS_PER_PAGE)
    : meals;

  useEffect(() => {
    setGridSoupsPageIndex(0);
    setGridMealsPageIndex(0);
  }, [locale]);

  useEffect(() => {
    setGridSoupsPageIndex((i) =>
      gridSoupsPageCount <= 1 ? 0 : Math.min(i, gridSoupsPageCount - 1),
    );
  }, [gridSoupsPageCount]);

  useEffect(() => {
    setGridMealsPageIndex((i) =>
      gridMealsPageCount <= 1 ? 0 : Math.min(i, gridMealsPageCount - 1),
    );
  }, [gridMealsPageCount]);

  useEffect(() => {
    if (gridSoupsPageCount <= 1) return undefined;
    const id = setInterval(() => {
      setGridSoupsPageIndex((i) => (i + 1) % gridSoupsPageCount);
    }, PORTRAIT_PAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [gridSoupsPageCount]);

  useEffect(() => {
    if (gridMealsPageCount <= 1) return undefined;
    const id = setInterval(() => {
      setGridMealsPageIndex((i) => (i + 1) % gridMealsPageCount);
    }, PORTRAIT_PAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [gridMealsPageCount]);

  const [timeTick, setTimeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTimeTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const now = useMemo(() => new Date(), [timeTick]); // eslint-disable-line react-hooks/exhaustive-deps -- timeTick updates every second for live clock
  const dateStr = formatDateOnly(now);
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const timeStr = `${hours}:${minutes}`;

  const allergensLegend = useMemo(
    () => getAllergensLegend(locale),
    [getAllergensLegend, locale],
  );
  const tickerText = useMemo(
    () => getTickerText(locale),
    [getTickerText, locale],
  );
  const hasTicker = Boolean(tickerText && tickerText.trim());
  const showAllergensBar = showAllergens && allergensLegend.length > 0;
  const headerTitle = useMemo(() => getTitle(locale), [getTitle, locale]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100vh",
          margin: 0,
          background: "#000",
          color: "#888",
          fontSize: "1.5rem",
        }}
      >
        {logo ? (
          <img
            src={logo}
            alt=""
            style={{ maxWidth: "50vw", maxHeight: "30vh", objectFit: "contain" }}
          />
        ) : (
          "Loading…"
        )}
      </div>
    );
  }
  if (error) {
    return (
      <ErrorView
        error={error}
        onRetry={refetch}
        countdownIntervalMs={COUNTDOWN_INTERVAL_MS}
        className="daily-menu-portrait"
      />
    );
  }

  const contentProps = {
    locale,
    defaultLocale,
    placeholderImageSoup: soupPlaceholderImage,
    placeholderImageMeal: mealPlaceholderImage,
    soldOutLabel: "",
    classNamePrefix: "daily-menu-portrait",
    showDescription,
    showPrices,
  };

  return (
    <div className="daily-menu-portrait">
      <header className="daily-menu-portrait__header">
        <div className="daily-menu-portrait__header-inner">
          <div className="daily-menu-portrait__header-left">
            <h1 className="daily-menu-portrait__title">
              {headerTitle}
              {dateStr && (
                <span className="daily-menu-portrait__date"> {dateStr}</span>
              )}
            </h1>
          </div>
          {activeMenuType?.label && (
            <span className="daily-menu-portrait__menu-type">
              {activeMenuType.label}
            </span>
          )}
          <div className="daily-menu-portrait__time" aria-hidden="true">
            {timeStr}
          </div>
        </div>
      </header>

      <div
        className="daily-menu-portrait__content"
        style={
          hasTicker || showAllergensBar
            ? {
                paddingBottom:
                  (showAllergensBar ? ALLERGENS_BAR_HEIGHT_PX : 0) +
                  (hasTicker ? TICKER_HEIGHT_PX : 0) +
                  16,
              }
            : undefined
        }
      >
        {weeklyRotationActive && showWeekly ? (
          <WeeklyMenuPortrait
            weekDays={weekDays}
            locale={locale}
            defaultLocale={defaultLocale}
          />
        ) : productDisplayType === "grid" ? (
          <DailyMenuContentGrid
            orientation="portrait"
            soups={visibleGridSoups}
            meals={visibleGridMeals}
            {...contentProps}
          />
        ) : (
          <DailyMenuContentList
            orientation="portrait"
            soups={visibleSoups}
            meals={visibleMeals}
            {...contentProps}
          />
        )}
      </div>

      {showAllergensBar && (
        <div
          className="daily-menu-portrait__allergens-bar daily-menu-screen__allergens-bar"
          style={hasTicker ? { bottom: TICKER_HEIGHT_PX } : undefined}
        >
          <div className="daily-menu-screen__allergens-bar-inner">
            {[...allergensLegend]
              .sort((a, b) => (a?.number ?? 0) - (b?.number ?? 0))
              .map((row, index, arr) => (
                <span
                  key={row?.number ?? row?.text ?? index}
                  className="daily-menu-screen__allergens-bar-item"
                >
                  {row?.number != null && (
                    <span className="daily-menu-screen__allergens-bar-num">
                      {row.number}
                    </span>
                  )}
                  {row?.number != null && row?.text != null && " – "}
                  {row?.text != null && (
                    <span className="daily-menu-screen__allergens-bar-text">
                      {row.text}
                    </span>
                  )}
                  {index < arr.length - 1 && (
                    <span className="daily-menu-screen__allergens-bar-sep">
                      {" | "}
                    </span>
                  )}
                </span>
              ))}
          </div>
        </div>
      )}

      {hasTicker && (
        <div
          className="daily-menu-portrait__ticker"
          style={{ height: TICKER_HEIGHT_PX }}
        >
          <Ticker text={tickerText} />
        </div>
      )}
    </div>
  );
};

export default DailyMenuScreenPortrait;
